import { useEffect, useRef } from 'react';

interface Props {
  title: string;
  /** Не задано — окно из одного вопроса. Пустого абзаца и ссылки описания
   *  на него тогда нет: диктор иначе объявлял бы пустое описание. */
  body?: string;
  confirmLabel: string;
  cancelLabel: string;
  /** `destructive` (по умолчанию) красит подтверждение красным: оно
   *  необратимо. `neutral` — для подтверждения, которое ничего не
   *  разрушает, как «Да» на вопросе «А ты точно родитель?»: красное там
   *  приучало бы не замечать красное, которое значит «необратимо». */
  tone?: 'destructive' | 'neutral';
  onConfirm: () => void;
  onCancel: () => void;
}

/** Своя разметка, а не элемент `<dialog>`: `showModal` в jsdom не
 *  реализован, и диалог остался бы непокрытым тестами ровно в том месте,
 *  ради которого он существует. Фокус, Esc и подложка сделаны руками. */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone = 'destructive',
  onConfirm,
  onCancel,
}: Props) {
  /** Фокус уходит на отмену, а не на подтверждение: подтверждение здесь —
   *  разрушительное действие, и «Enter вслепую» не должен его выполнять. */
  const cancelRef = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  /** Обработчик держится в ссылке, а эффект — с пустыми зависимостями.
   *  Ставить сюда сам `onCancel` нельзя: любая нестабильная ссылка сверху
   *  пересоздаёт эффект, а его снятие возвращает фокус — то есть выбрасывает
   *  ребёнка из диалога при каждой перерисовке родителя. */
  const cancelRef2 = useRef(onCancel);
  cancelRef2.current = onCancel;

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    cancelRef.current?.focus();

    /** Табуляция заперта внутри окна. Без этого третье нажатие Tab уводит
     *  на кнопку под подложкой: на экране тренировки там кнопки ответа, и
     *  ребёнок с клавиатурой отвечает на задание, которого не видит.
     *  Возврат фокуса при закрытии — из того же ряда: без него фокус падает
     *  на `<body>`, и первый же Tab снова ведёт на выход из тренировки. */
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        cancelRef2.current();
        return;
      }
      if (event.key !== 'Tab' || !box.current) return;
      const focusable = [...box.current.querySelectorAll<HTMLElement>('button')];
      /** Ветка недостижима и оставлена намеренно: у окна ровно две кнопки,
       *  их подписи обязательны в `Props`. Мутационный проход отметил её
       *  как непокрытую — она и не покрываема, пока обе подписи
       *  обязательны. Проверка стоит на случай, если у окна появится вид
       *  без кнопок: тогда обход по Tab упёрся бы в `focusable[0]` от
       *  пустого списка. */
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      /** Здесь я ошибся, и ошибку нашло внешнее ревью. Подмену
       *  `shiftKey &&` на `||` я объявил неразличимой — кнопок в окне две,
       *  «следующая» и «последняя» совпадают. Рассуждение неполное: при
       *  `||` ветка заворота срабатывает на ЛЮБОМ Shift+Tab, и обратный
       *  ход с последней кнопки не идёт на первую, а застревает на месте.
       *  Проверено ломанием, тест на это теперь есть.
       *
       *  Вывод шире случая: «мутант неразличим» — единственное суждение
       *  здесь, которое нельзя показать ломанием, и потому единственное,
       *  которое стоит перепроверять чужими глазами. */
      if (event.shiftKey && (active === first || !box.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !box.current.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      returnTo?.focus();
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-10 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby={body ? 'confirm-body' : undefined}
        className="flex w-full max-w-[24rem] flex-col gap-3 rounded-2xl bg-card p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="confirm-title" className="m-0 text-lg font-semibold">
          {title}
        </h2>
        {body && (
          <p id="confirm-body" className="m-0 text-[0.95rem] text-muted">
            {body}
          </p>
        )}
        <div className="mt-2 grid gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="min-h-12 rounded-xl bg-accent p-3 font-semibold text-white"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`min-h-12 rounded-xl border-2 border-line bg-card p-3 font-semibold ${
              tone === 'destructive' ? 'text-incorrect' : 'text-ink'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
